'use client';

/**
 * Unified loading overlay — jeden vizuální jazyk pro login, logout a běžný loading.
 *
 * Tři varianty se liší jen:
 * - Směrem kruhů (converge = sbíhají se, ripple = rozšiřují se, pulse = na místě)
 * - Centrální ikonou (zámek pro login, dveře pro logout, ring pro loading)
 * - Textem (login/logout mají text + dots, loading je bez textu)
 *
 * Použití:
 *   <LoadingOverlay variant="login"  label={t('loggingIn')}  visible={busy} />
 *   <LoadingOverlay variant="logout" label={t('loggingOut')} visible={isLoggingOut} />
 *   <LoadingOverlay variant="loading" visible={isLoading} />
 */

export type OverlayVariant = 'login' | 'logout' | 'loading';

interface LoadingOverlayProps {
  variant: OverlayVariant;
  label?: string;
  visible: boolean;
}

/** Konfigurace variant — keyframe, ikona, backdrop opacity */
const VARIANT_CONFIG: Record<OverlayVariant, {
  ringAnimation: string;
  ringDuration: string;
  icon: 'lock' | 'door' | 'ring';
  backdropClass: string;
  showText: boolean;
}> = {
  login: {
    ringAnimation: 'ringConverge',
    ringDuration: '2.4s',
    icon: 'lock',
    backdropClass: 'bg-bark/40 backdrop-blur-md',
    showText: true,
  },
  logout: {
    ringAnimation: 'ringRipple',
    ringDuration: '2.4s',
    icon: 'door',
    backdropClass: 'bg-bark/50 backdrop-blur-md',
    showText: true,
  },
  loading: {
    ringAnimation: 'ringPulse',
    ringDuration: '2s',
    icon: 'ring',
    backdropClass: 'bg-bark/15 backdrop-blur-[0.5px]',
    showText: false,
  },
};

/** Visací zámek — login ikona (odemykání) */
function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7 text-cream" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path
        d="M8 11V7a4 4 0 0 1 8 0"
        style={{ animation: 'shackleOpen 2s ease-in-out infinite', transformOrigin: '12px 11px' }}
      />
      <rect x={5} y={11} width={14} height={10} rx={2} />
      <circle cx={12} cy={16} r={1.2} fill="currentColor" />
    </svg>
  );
}

/** Dveře/šipka ven — logout ikona (odchod) */
function DoorIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7 text-cream" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9" />
      <path d="M10 12h11" />
      <path d="M18 8l4 4-4 4" />
    </svg>
  );
}

/** Rotující ring — loading ikona (běžný progress) */
function RingIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7 text-cream" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" style={{ animation: 'iconSpin 1s linear infinite', transformOrigin: '12px 12px' }} />
    </svg>
  );
}

const ICONS = { lock: LockIcon, door: DoorIcon, ring: RingIcon };

export default function LoadingOverlay({ variant, label, visible }: LoadingOverlayProps) {
  if (!visible) return null;

  const config = VARIANT_CONFIG[variant];
  const Icon = ICONS[config.icon];

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center ${config.backdropClass} animate-[fadeIn_0.25s_ease-out]`}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-8 animate-[overlayEnter_0.4s_cubic-bezier(0.34,1.56,0.64,1)]">
        {/* Portal — relativní kontejner pro ringy + orb */}
        <div className="relative w-32 h-32 flex items-center justify-center">
          {/* 3 ringy — converge (login), ripple (logout), nebo pulse (loading) */}
          <div
            className="absolute inset-0 rounded-full border-2 border-moss/40"
            style={{ animation: `${config.ringAnimation} ${config.ringDuration} ease-out infinite`, animationDelay: '0s' }}
          />
          <div
            className="absolute inset-0 rounded-full border-2 border-moss/30"
            style={{ animation: `${config.ringAnimation} ${config.ringDuration} ease-out infinite`, animationDelay: '0.8s' }}
          />
          <div
            className="absolute inset-0 rounded-full border-2 border-moss/20"
            style={{ animation: `${config.ringAnimation} ${config.ringDuration} ease-out infinite`, animationDelay: '1.6s' }}
          />

          {/* Centrální orb s ikonou */}
          <div
            className="relative w-12 h-12 rounded-full bg-gradient-to-br from-moss to-moss-dark flex items-center justify-center animate-[orbBreathe_2s_ease-in-out_infinite]"
          >
            <Icon />
          </div>
        </div>

        {/* Text + flowing dots — jen pro login/logout */}
        {config.showText && label && (
          <div className="flex flex-col items-center gap-2 animate-[elegantFadeIn_0.5s_ease-out_0.3s_both]">
            <p className="text-cream text-sm font-light tracking-[0.2em] uppercase">
              {label}
            </p>
            <div className="flex gap-1.5 text-cream/60 text-xs">
              <span className="animate-[dotsFlow_1.5s_ease-in-out_infinite]" style={{ animationDelay: '0s' }}>●</span>
              <span className="animate-[dotsFlow_1.5s_ease-in-out_infinite]" style={{ animationDelay: '0.2s' }}>●</span>
              <span className="animate-[dotsFlow_1.5s_ease-in-out_infinite]" style={{ animationDelay: '0.4s' }}>●</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
